import { Injectable, DestroyRef, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SignalRService } from './signalr.service';

export type NotificationType = 'orderUpdated' | 'emailSent' | 'orderStatusUpdated';

export interface AppNotification {
  id: number;
  type: NotificationType;
  message: string;
  timestamp: Date;
  read: boolean;
}

const MAX_NOTIFICATIONS = 200;
let nextId = 1;

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly signalRService = inject(SignalRService);

  private readonly _notifications = signal<AppNotification[]>([]);
  readonly notifications = this._notifications.asReadonly();
  readonly unreadCount = computed(() => this._notifications().filter((n) => !n.read).length);

  constructor() {
    this.signalRService.orderUpdated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((payload) => {
      const parts: string[] = [`Order #${payload.id}`];
      if (payload.eventType) parts.push(`— ${payload.eventType}`);
      if (payload.securityName) parts.push(`· ${payload.securityName}`);
      if (payload.portfolioShortName) parts.push(`(${payload.portfolioShortName})`);
      if (payload.statusName) parts.push(`→ ${payload.statusName}`);
      this.push('orderUpdated', parts.join(' '));
    });

    this.signalRService.emailSent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((details) => {
      const orderList = details.orderIds.join(', ');
      this.push(
        'emailSent',
        `Email sent to ${details.broker} for order${details.orderIds.length > 1 ? 's' : ''} #${orderList}`
      );
    });

    this.signalRService.orderStatusUpdated$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((details) => {
      this.push('orderStatusUpdated', `Order #${details.orderId} status → ${details.status.name}`);
    });
  }

  markAllRead(): void {
    this._notifications.update((list) => list.map((n) => ({ ...n, read: true })));
  }

  clear(): void {
    this._notifications.set([]);
  }

  private push(type: NotificationType, message: string): void {
    const notification: AppNotification = { id: nextId++, type, message, timestamp: new Date(), read: false };
    this._notifications.update((list) => {
      const updated = [notification, ...list];
      return updated.length > MAX_NOTIFICATIONS ? updated.slice(0, MAX_NOTIFICATIONS) : updated;
    });
  }
}
